import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  LinearProgress
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import StopIcon from '@mui/icons-material/Stop';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import SocketTTSStream from '../services/SocketTTSStream';
import { useSocket } from '../context/SocketContext';
import { getVoiceForMessage } from '../utils/voiceUtils';
import { markdownToSSML, containsMarkdown } from '../utils/markdownToSSML';
import { chunkTextForTTS, needsChunking } from '../utils/textChunking';
import api, { getElevenLabsVoices } from '../services/api';

const TextToSpeech = ({ 
  text, 
  disabled = false,
  voiceId = null, // No default voice - will be selected based on settings
  senderRole = null, // For automatic voice selection
  userSettings = null, // User-specific voice settings
  platformSettings = null, // Platform voice settings
  therapistSettings = null, // Therapist voice settings for client conversations
  clientId = null, // Client ID for therapist voice overrides
  showVoiceSelector = false,
  size = 'medium', // 'small', 'medium', 'large'
  useStreaming = true, // Toggle between streaming and REST API
  autoplay = false, // Whether to automatically start playing
  onPlaybackComplete = null, // Callback when playback completes
  setPlayingAudio = null, // Callback to register/unregister playing audio
  token = null, // JWT token for API calls
  useBackendTTS = true, // Flag to use backend TTS abstraction
  ttsInterface = 'client_chat' // Interface context for TTS optimization
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [voiceMenuAnchor, setVoiceMenuAnchor] = useState(null);
  const [streamingProgress, setStreamingProgress] = useState(0);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  // Provider is now determined by MessageList and passed via useStreaming prop
  const hasAutoplayedRef = useRef(false);
  const chunksRef = useRef([]);
  
  // Determine voice to use.
  //
  // Precedence (changed 2026-04-26):
  //   1. voiceId prop — when set, this is the authoritative resolution from
  //      the parent (e.g. ChatInterface's assignedTherapistVoiceId, which is
  //      itself resolver-resolved server-side and honours the client's catalog
  //      pick at hierarchy level 1).
  //   2. getVoiceForMessage — the legacy browser-side hierarchy in
  //      voiceUtils.js. Used when no voiceId prop is supplied (ad-hoc preview
  //      callers, admin testing).
  //
  // Previously the order was reversed, which meant the legacy resolver
  // always won and the client's picked voice was ignored.
  const determineVoiceId = () => {
    if (voiceId) {
      console.log('[TextToSpeech] Using voiceId prop:', voiceId);
      return voiceId;
    }

    if (senderRole && (userSettings || platformSettings || therapistSettings)) {
      const message = { senderRole };
      const selectedVoice = getVoiceForMessage(message, userSettings, platformSettings, therapistSettings, clientId);
      if (selectedVoice) return selectedVoice;
    }

    console.log('[TextToSpeech] No voice configured for this message');
    return null;
  };
  
  const [selectedVoice, setSelectedVoice] = useState(determineVoiceId());
  
  // Update selected voice when props change
  useEffect(() => {
    const newVoiceId = determineVoiceId();
    if (newVoiceId !== selectedVoice) {
      console.log('[TextToSpeech] Voice changed from', selectedVoice, 'to', newVoiceId);
      setSelectedVoice(newVoiceId);
    }
  }, [senderRole, userSettings, platformSettings, therapistSettings, clientId, voiceId]);
  
  const audioRef = useRef(null);
  const elevenLabsWSRef = useRef(null);
  const socket = useSocket();

  // Streaming TTS is relay-only: it streams through the server over the
  // authenticated socket (key-removal plan Phase 3 — no ElevenLabs key in the
  // bundle). The legacy direct browser→ElevenLabs WebSocket path is gone.
  const useRelayTransport = !!socket;
  const createStreamClient = (voice) =>
    new SocketTTSStream(socket, voice, 'eleven_turbo_v2_5', true, false,
      { role: senderRole || 'ai_therapist', interface: ttsInterface || 'client_chat' });

  // Use the streaming prop directly from MessageList - it has already done provider detection
  // This eliminates the race condition where TextToSpeech and MessageList detect separately.
  // Stream only when the socket is available; a socketless moment falls through
  // to the server-side REST /tts/generate route, so it degrades cleanly.
  const effectiveUseStreaming = useStreaming && useRelayTransport;
  
  // Track when useStreaming prop changes for debugging
  useEffect(() => {
    console.log('[TextToSpeech] useStreaming prop changed to:', useStreaming, 'autoplay:', autoplay);
  }, [useStreaming, autoplay]);

  // Initialize WebSocket client
  useEffect(() => {
    // Only initialize WebSocket if we're actually using streaming
    if (effectiveUseStreaming) {
      console.log(`[TextToSpeech] Initializing streaming client (transport: ${useRelayTransport ? 'relay' : 'direct'})`);
      elevenLabsWSRef.current = createStreamClient(selectedVoice);
      
      // Set up callbacks
      elevenLabsWSRef.current.setCallbacks({
        onStart: () => {
          setIsLoading(true);
          setError('');
          setStreamingProgress(0);
          console.log('Streaming started');
        },
        onAudioChunk: (chunk, chunkCount) => {
          setStreamingProgress(chunkCount);
        },
        onPlaybackStart: () => {
          setIsPlaying(true);
          setIsLoading(false);
          console.log('Playback started');
          // Register this audio instance as playing
          if (setPlayingAudio) {
            setPlayingAudio(elevenLabsWSRef.current);
          }
        },
        onPlaybackEnd: () => {
          setIsPlaying(false);
          console.log('Playback ended');
        },
        onComplete: () => {
          setIsPlaying(false);
          setIsLoading(false);
          setStreamingProgress(0);
          // Unregister this audio instance
          if (setPlayingAudio) {
            setPlayingAudio(null);
          }
        },
        onError: (error) => {
          setError(`Streaming error: ${error.message}`);
          setIsLoading(false);
          setIsPlaying(false);
          setStreamingProgress(0);
          // Unregister this audio instance on error
          if (setPlayingAudio) {
            setPlayingAudio(null);
          }
        }
      });
    }

    return () => {
      if (elevenLabsWSRef.current) {
        elevenLabsWSRef.current.stop();
      }
    };
  }, [effectiveUseStreaming, selectedVoice, useRelayTransport]);

  // Load available voices
  useEffect(() => {
    if (showVoiceSelector) {
      loadVoices();
    }
  }, [showVoiceSelector]);

  // Effect to update selectedVoice if props change
  useEffect(() => {
    setSelectedVoice(determineVoiceId());
  }, [voiceId, senderRole, userSettings, platformSettings, therapistSettings, clientId]);

  const loadVoices = async () => {
    try {
      // Server-backed, cached voices list — no provider key in the browser
      // (key-removal plan, Phase 1.3). Mapped to the legacy ElevenLabs shape.
      const voices = await getElevenLabsVoices();
      setAvailableVoices(voices || []);
      console.log('Voices loaded:', voices?.length || 0);
    } catch (err) {
      console.error('Failed to load voices:', err);
    }
  };

  // Preprocess text for TTS
  const preprocessText = useCallback((inputText) => {
    if (!inputText) return '';
    
    // Check if text contains markdown and convert to SSML if enabled
    if (containsMarkdown(inputText) && platformSettings?.markdownToSSMLEnabled !== false) {
      return markdownToSSML(inputText, {
        headerPause: '0.5s',
        listItemPause: '0.3s',
        paragraphPause: '0.4s',
        enableEmphasis: true
      });
    }
    
    return inputText;
  }, [platformSettings]);

  // Streaming speech handler
  const handleStreamingSpeak = useCallback(async () => {
    // Performance optimization: Track timing
    const startTime = performance.now();
    
    if (!text || !text.trim()) {
      setError('No text to speak');
      return;
    }
    
    if (!selectedVoice) {
      setError('No voice configured. Please set up voices in Platform Settings.');
      return;
    }

    if (isPlaying || isLoading) {
      // Stop current streaming
      if (elevenLabsWSRef.current) {
        elevenLabsWSRef.current.stop();
      }
      setIsPlaying(false);
      setIsLoading(false);
      setStreamingProgress(0);
      // Unregister this audio instance
      if (setPlayingAudio) {
        setPlayingAudio(null);
      }
      return;
    }

    try {
      console.log(`[TextToSpeech] Streaming via ${useRelayTransport ? 'server relay' : 'direct'} transport`);

      // For streaming, just use plain text to avoid SSML tags being read
      // The WebSocket API seems to have issues with SSML parsing
      const processedText = text;
      
      // Update voice if changed
      if (elevenLabsWSRef.current.voiceId !== selectedVoice) {
        elevenLabsWSRef.current = createStreamClient(selectedVoice);
        elevenLabsWSRef.current.setCallbacks({
          onStart: () => {
            setIsLoading(true);
            setError('');
            setStreamingProgress(0);
          },
          onAudioChunk: (chunk, chunkCount) => {
            setStreamingProgress(chunkCount);
          },
          onPlaybackStart: () => {
            const latency = performance.now() - startTime;
            console.log(`[TextToSpeech] Stream started in ${latency.toFixed(0)}ms`);
            setIsPlaying(true);
            setIsLoading(false);
            // Register this audio instance as playing
            if (setPlayingAudio) {
              setPlayingAudio(elevenLabsWSRef.current);
            }
          },
          onPlaybackEnd: () => {
            setIsPlaying(false);
          },
          onComplete: () => {
            setIsPlaying(false);
            setIsLoading(false);
            setStreamingProgress(0);
            // Unregister this audio instance
            if (setPlayingAudio) {
              setPlayingAudio(null);
            }
            if (onPlaybackComplete) {
              onPlaybackComplete();
            }
          },
          onError: (error) => {
            setError(`Streaming error: ${error.message}`);
            setIsLoading(false);
            setIsPlaying(false);
            setStreamingProgress(0);
            // Unregister this audio instance on error
            if (setPlayingAudio) {
              setPlayingAudio(null);
            }
          }
        });
      }

      // Don't use SSML for streaming - just send plain text
      console.log('TextToSpeech: Streaming plain text (no SSML due to API limitations)');
      await elevenLabsWSRef.current.startStreaming(processedText, { enable_ssml_parsing: false });
      
    } catch (err) {
      console.error('Streaming error:', err);
      setError(`Failed to start streaming: ${err.message}`);
      setIsLoading(false);
    }
  }, [text, selectedVoice, isPlaying, isLoading, preprocessText]);

  // Play individual chunk from chunked audio
  const playChunk = useCallback(async (chunkIndex, chunks, voiceId, enableSSML) => {
    if (chunkIndex >= chunks.length) {
      // All chunks played
      setIsPlaying(false);
      setIsLoading(false);
      setCurrentChunk(0);
      setTotalChunks(0);
      chunksRef.current = [];
      if (setPlayingAudio) {
        setPlayingAudio(null);
      }
      if (onPlaybackComplete) {
        onPlaybackComplete();
      }
      return;
    }

    const chunkText = chunks[chunkIndex];
    console.log(`[TextToSpeech] Playing chunk ${chunkIndex + 1}/${chunks.length}`);

    try {
      let response;

      {
        // Server-side synthesis only — the direct-ElevenLabs fallback that
        // lived here required the API key in the web bundle and only ran when
        // there was no auth token (an unauthenticated user shouldn't be
        // synthesising speech anyway). Key-removal plan, Phase 1.3.
        const result = await api.post('/tts/generate', {
          text: chunkText,
          voiceId,
          role: senderRole || 'ai_therapist',
          interface: ttsInterface || 'client_chat'
        }, token);

        if (result?.data && result.data.success && result.data.audio) {
          const byteCharacters = atob(result.data.audio);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });

          response = {
            ok: true,
            blob: async () => audioBlob
          };
        } else {
          throw new Error(result?.data?.msg || 'Failed to generate speech');
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        URL.revokeObjectURL(audioUrl);
        // Play next chunk
        setCurrentChunk(chunkIndex + 2); // +2 because index is 0-based but display is 1-based
        playChunk(chunkIndex + 1, chunks, voiceId, enableSSML);
      };

      audioRef.current.onerror = () => {
        setError(`Failed to play chunk ${chunkIndex + 1}`);
        setIsPlaying(false);
        setIsLoading(false);
        URL.revokeObjectURL(audioUrl);
        if (setPlayingAudio) {
          setPlayingAudio(null);
        }
      };

      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);

      // Register stop function for chunked playback
      if (setPlayingAudio && chunkIndex === 0) {
        setPlayingAudio({
          stop: () => {
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            }
            setIsPlaying(false);
            setCurrentChunk(0);
            setTotalChunks(0);
            chunksRef.current = [];
          }
        });
      }

    } catch (err) {
      console.error(`Error playing chunk ${chunkIndex + 1}:`, err);
      // iOS/Safari block audio that isn't started by a direct user gesture, so
      // hands-free autoplay can reject with NotAllowedError. That's expected, not
      // a failure to surface — leave the per-message play button so the user can
      // tap to hear it. Only show an error for genuine playback failures.
      const isAutoplayBlocked =
        err?.name === 'NotAllowedError' ||
        /not allowed by the user agent|user denied permission/i.test(err?.message || '');
      if (isAutoplayBlocked && autoplay) {
        console.warn('[TextToSpeech] Autoplay blocked by the browser (no user gesture) — leaving play button available.');
      } else {
        setError(`Failed to play chunk ${chunkIndex + 1}: ${err.message}`);
      }
      setIsLoading(false);
      setIsPlaying(false);
      if (setPlayingAudio) {
        setPlayingAudio(null);
      }
    }
  }, [useBackendTTS, token, senderRole, ttsInterface, platformSettings, setPlayingAudio, onPlaybackComplete, autoplay]);

  // REST API speech handler (fallback)
  const handleRestSpeak = useCallback(async () => {
    if (!text || !text.trim()) {
      setError('No text to speak');
      return;
    }
    
    if (!selectedVoice) {
      setError('No voice configured. Please set up voices in Platform Settings.');
      return;
    }

    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      // Unregister this audio instance
      if (setPlayingAudio) {
        setPlayingAudio(null);
      }
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Preprocess text
      const processedText = preprocessText(text);
      const enableSSML = processedText.startsWith('<speak>');

      // Check if text needs chunking
      if (needsChunking(processedText)) {
        console.log('[TextToSpeech] Text exceeds size limit, using chunking');
        const chunks = chunkTextForTTS(processedText);
        chunksRef.current = chunks;
        setTotalChunks(chunks.length);
        setCurrentChunk(1);
        await playChunk(0, chunks, selectedVoice, enableSSML);
        return;
      }

      // Normal flow for text under 5000 chars
      chunksRef.current = [];
      setTotalChunks(0);
      setCurrentChunk(0);

      let response;

      {
        // Server-side synthesis only (backend TTS abstraction layer). The
        // direct-ElevenLabs fallbacks that lived here required the API key in
        // the web bundle; a backend failure now surfaces as an error instead
        // of silently burning the key from the browser. Key-removal plan,
        // Phase 1.3.
        console.log('[TextToSpeech] Using backend TTS');
        const result = await api.post('/tts/generate', {
          text: processedText,
          voiceId: selectedVoice,
          role: senderRole || 'ai_therapist',
          interface: ttsInterface || 'client_chat'
        }, token);

        // api.post returns full axios response, so access .data
        if (result?.data && result.data.success && result.data.audio) {
          console.log('[TextToSpeech] Successfully generated audio via backend TTS');
          // Convert base64 to blob
          const byteCharacters = atob(result.data.audio);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });

          response = {
            ok: true,
            blob: async () => audioBlob
          };
        } else {
          throw new Error(result?.data?.msg || 'Failed to generate speech');
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setIsLoading(false);
        URL.revokeObjectURL(audioUrl);
        // Unregister this audio instance
        if (setPlayingAudio) {
          setPlayingAudio(null);
        }
        if (onPlaybackComplete) {
          onPlaybackComplete();
        }
      };
      
      audioRef.current.onerror = () => {
        setError('Failed to play audio');
        setIsPlaying(false);
        setIsLoading(false);
        URL.revokeObjectURL(audioUrl);
        // Unregister this audio instance
        if (setPlayingAudio) {
          setPlayingAudio(null);
        }
      };
      
      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
      // Register this audio instance as playing (for REST API, we register the component itself)
      if (setPlayingAudio) {
        setPlayingAudio({ stop: () => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          setIsPlaying(false);
        }});
      }
      
    } catch (err) {
      console.error('Text-to-speech error:', err);
      setError(`Failed to generate speech: ${err.message}`);
      setIsLoading(false);
    }
  }, [text, selectedVoice, isPlaying, preprocessText, platformSettings]);

  // Main speak handler
  const handleSpeak = useCallback(() => {
    if (effectiveUseStreaming) {
      return handleStreamingSpeak();
    } else {
      return handleRestSpeak();
    }
  }, [effectiveUseStreaming, handleStreamingSpeak, handleRestSpeak]);
  
  // Effect to handle autoplay
  useEffect(() => {
    if (autoplay && text && !hasAutoplayedRef.current && !disabled) {
      console.log('TextToSpeech: Triggering autoplay for text:', text.substring(0, 50) + '...', 'autoplay:', autoplay);
      console.log('TextToSpeech: Autoplay using effectiveUseStreaming:', effectiveUseStreaming);
      console.log('TextToSpeech: Using handler:', effectiveUseStreaming ? 'streaming' : 'REST');
      console.log('TextToSpeech: Selected voice for autoplay:', selectedVoice);
      
      hasAutoplayedRef.current = true;
      // Small delay to ensure component is fully mounted and WebSocket is initialized if needed
      setTimeout(() => {
        handleSpeak();
      }, 100);
    }
  }, [autoplay, text, disabled, effectiveUseStreaming, selectedVoice, handleSpeak]); // Simple dependencies
  
  // Reset autoplay flag when autoplay prop changes from true to false
  useEffect(() => {
    if (!autoplay) {
      hasAutoplayedRef.current = false;
    }
  }, [autoplay]);

  const handleVoiceMenuOpen = (event) => {
    setVoiceMenuAnchor(event.currentTarget);
  };

  const handleVoiceMenuClose = () => {
    setVoiceMenuAnchor(null);
  };

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice.voice_id);
    handleVoiceMenuClose();
  };

  const handleCloseError = () => {
    setError('');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src) {
          URL.revokeObjectURL(audioRef.current.src);
        }
      }
      if (elevenLabsWSRef.current) {
        elevenLabsWSRef.current.stop();
      }
    };
  }, []);

  const getIconSize = () => {
    switch (size) {
      case 'small': return 'small';
      case 'large': return 'large';
      default: return 'medium';
    }
  };

  const getButtonSize = () => {
    switch (size) {
      case 'small': return { width: 32, height: 32 };
      case 'large': return { width: 48, height: 48 };
      default: return { width: 40, height: 40 };
    }
  };

  // Don't render if no text provided
  if (!text || !text.trim()) {
    return null;
  }

  const getTooltipText = () => {
    if (!selectedVoice) {
      return 'No voice configured - click to see error';
    }
    if (isLoading) {
      return useStreaming ? 
        `Generating speech... (${streamingProgress} chunks)` : 
        'Generating speech...';
    }
    return isPlaying ? 'Stop speaking' : 'Speak text';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={getTooltipText()}>
        <IconButton
          onClick={handleSpeak}
          disabled={disabled}
          color={isPlaying ? 'primary' : 'default'}
          size={getIconSize()}
          sx={{
            ...getButtonSize(),
            backgroundColor: isPlaying ? 'primary.light' : 'transparent',
            '&:hover': {
              backgroundColor: isPlaying ? 'primary.main' : 'action.hover',
            },
            ...(isLoading && {
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }),
          }}
        >
          {isLoading ? (
            <VolumeUpIcon />
          ) : isPlaying ? (
            <StopIcon />
          ) : (
            <VolumeUpIcon />
          )}
        </IconButton>
      </Tooltip>

      {totalChunks > 1 && isPlaying && (
        <Tooltip title={`Playing chunk ${currentChunk} of ${totalChunks}`}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: '60px' }}>
            {currentChunk}/{totalChunks}
          </Typography>
        </Tooltip>
      )}

      {showVoiceSelector && availableVoices.length > 0 && (
        <>
          <Tooltip title="Select voice">
            <IconButton
              onClick={handleVoiceMenuOpen}
              size={getIconSize()}
              disabled={disabled || isLoading}
            >
              <SettingsVoiceIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={voiceMenuAnchor}
            open={Boolean(voiceMenuAnchor)}
            onClose={handleVoiceMenuClose}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: 250,
              },
            }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2" color="text.secondary">
                Select Voice
              </Typography>
            </MenuItem>
            <Divider />
            {availableVoices.map((voice) => (
              <MenuItem
                key={voice.voice_id}
                onClick={() => handleVoiceSelect(voice)}
                selected={voice.voice_id === selectedVoice}
              >
                <ListItemText
                  primary={voice.name}
                  secondary={`${voice.labels?.gender || 'Unknown'} • ${voice.labels?.age || 'Unknown age'}`}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TextToSpeech;
